import React, { useRef, useEffect, useState } from "react";
import CanvasDraw from "react-canvas-draw";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import CustomModal from "./CustomModal";
import { toast } from "react-toastify";

interface SignatureModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (dataUrl: string) => Promise<void>;
  title?: string;
  existingSignature?: string | null;
}

const SignatureModal: React.FC<SignatureModalProps> = ({
  open,
  onClose,
  onSave,
  title = "Assinatura",
  existingSignature,
}) => {
  const canvasRef = useRef<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
  const lockOrientation = async () => {
    const { screen } = window as any;
    if (screen.orientation && screen.orientation.lock) {
      try {
        await screen.orientation.lock("landscape");
      } catch (error) {
        console.warn("Não foi possível forçar a rotação:", error);
      }
    }
  };

  if (open) {
    lockOrientation();
  }

  return () => {
    const { screen } = window as any;
    if (screen.orientation && screen.orientation.unlock) {
      screen.orientation.unlock();
    }
  };
}, [open]);

  useEffect(() => {
    if (open && existingSignature && !isEditing) {
      setTimeout(() => {
        if (canvasRef.current) {
          canvasRef.current.clear();
          canvasRef.current.loadSaveData(existingSignature);
        }
      }, 100);
    }
  }, [open, existingSignature, isEditing]);

  const handleSave = async () => {
    if (canvasRef.current) {
      const saveData = canvasRef.current.getSaveData();
      const data = JSON.parse(saveData);
      const isEmpty = !data.lines || data.lines.length === 0;

      if (isEmpty) {
        toast.warn("Não é possível salvar uma assinatura vazia.");
        return;
      }

      setIsSaving(true);
      try {
        const dataUrl = canvasRef.current.getDataURL();
        await onSave(dataUrl);
        toast.success("Assinatura salva com sucesso!");
        setIsEditing(false);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleClear = () => {
    canvasRef.current?.clear();
    setIsEditing(true);
  };

  const handleDelete = async () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
    }
    setIsEditing(true);
    setIsSaving(true);
    try {
      await onSave("");
      toast.success("Assinatura apagada com sucesso!");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={title}
       fullScreen={true}
      actions={
        existingSignature && !isEditing ? (
          <>
            <Button onClick={handleDelete} color="error">
              Apagar
            </Button>
            <Button onClick={onClose} variant="contained">
              Fechar
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClear} color="warning" disabled={isSaving}>
              Limpar
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={20} /> : null}
            >
              {isSaving ? "Salvando..." : "Salvar"}
            </Button>
          </>
        )
      }
    >
      {existingSignature && !isEditing ? (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Assinatura atual:
          </Typography>
          <Box sx={{ textAlign: "center" }}>
            <img
              src={existingSignature}
              alt="Assinatura salva"
              style={{ maxWidth: "100%", maxHeight: 200 }}
            />
          </Box>
        </Box>
      ) : (
<Box
  sx={{
    width: "100vw",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: "#fff",
  }}
>
  <CanvasDraw
    brushColor="black"
    brushRadius={2}
    canvasWidth={window.innerWidth}
    canvasHeight={window.innerHeight - 100}
    hideInterface
    hideGrid
    ref={canvasRef}
  />
</Box>
      )}
    </CustomModal>
  );
};

export default SignatureModal;
